import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { CampaignFunnelDailyRow, CardBand, CardRoasSeriesPoint, CardSeries } from '../types';
import { currencyPrefix } from '../logic';

const INK = '#0F172A';
const MUTED = '#64748B';
const FAINT = '#94A3B8';
const BORDER = '#E2E8F0';
const PAPER = '#FFFFFF';
const INDIGO = '#6366F1';
const TW = '#2563EB';
const G_WEEK = '#B45309';
const G_LEVEL = '#D97706';
const BAD = '#DC2626';

type NullableNumber = number | null | undefined;
type RangeDays = 7 | 30 | 90;

const isNumber = (value: NullableNumber): value is number =>
  value != null && Number.isFinite(value);

const roasExact = (value: NullableNumber): string =>
  isNumber(value) ? `${value.toFixed(2)}x` : '-';

const moneyText = (value: NullableNumber, store: string | null): string =>
  isNumber(value) ? `${currencyPrefix(store)}${Math.round(value).toLocaleString()}` : '-';

/**
 * Straight segments, for the two daily lines. A curve fitted through daily return would bow
 * between the points it connects and put the campaign at values it never had: on a spiky series
 * the fit overshoots each peak and undershoots each trough by more than the gap between two real
 * days. The 30-day line is already an average and is drawn as a curve, which is what it is.
 */
function linearPath(values: NullableNumber[], xPoint: (index: number) => number, yPoint: (value: number) => number): string {
  let path = '';
  let open = false;
  values.forEach((value, index) => {
    if (!isNumber(value)) { open = false; return; }
    path += `${open ? 'L' : 'M'}${xPoint(index).toFixed(2)},${yPoint(value).toFixed(2)} `;
    open = true;
  });
  return path.trim();
}

function smoothPath(values: NullableNumber[], xPoint: (index: number) => number, yPoint: (value: number) => number): string {
  let path = '';
  let run: { x: number; y: number }[] = [];

  const flush = () => {
    if (run.length === 1) {
      path += `M${run[0].x},${run[0].y} `;
    } else if (run.length > 1) {
      path += `M${run[0].x},${run[0].y} `;
      for (let index = 0; index < run.length - 1; index += 1) {
        const previous = run[index - 1] ?? run[index];
        const first = run[index];
        const second = run[index + 1];
        const next = run[index + 2] ?? second;
        const controlOneX = first.x + (second.x - previous.x) / 6;
        const controlOneY = first.y + (second.y - previous.y) / 6;
        const controlTwoX = second.x - (next.x - first.x) / 6;
        const controlTwoY = second.y - (next.y - first.y) / 6;
        path += `C${controlOneX.toFixed(2)},${controlOneY.toFixed(2)} ${controlTwoX.toFixed(2)},${controlTwoY.toFixed(2)} ${second.x.toFixed(2)},${second.y.toFixed(2)} `;
      }
    }
    run = [];
  };

  values.forEach((value, index) => {
    if (!isNumber(value)) {
      flush();
      return;
    }
    run.push({ x: xPoint(index), y: yPoint(value) });
  });
  flush();
  return path.trim();
}

function parseDate(value: string | undefined): Date | null {
  if (!value) return null;
  const date = value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date: Date, weekday: boolean): string {
  return date.toLocaleDateString('en-US', weekday
    ? { weekday: 'short', month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric' });
}

export function dateLabel(value: string | undefined, weekday = false): string {
  const date = parseDate(value);
  return date ? formatDate(date, weekday) : '-';
}

// Every trailing figure in the tooltip is headed by the days it actually covers, so a reader
// never has to work out what "L7D" means or assume a window that is still filling up. A campaign
// six days into its life has a six-day window, and the header says six days.
function rangeLabel(end: string, days: number): string {
  const last = parseDate(end);
  if (!last) return '-';
  if (days <= 1) return formatDate(last, true);
  const first = new Date(last.getTime());
  first.setDate(first.getDate() - (days - 1));
  return `${formatDate(first, false)} to ${formatDate(last, false)}`;
}

function sliceRange<T>(values: T[], range: RangeDays): T[] {
  return values.slice(Math.max(0, values.length - range));
}

function latestValue(values: NullableNumber[]): number | null {
  return [...values].reverse().find(isNumber) ?? null;
}

// ---------------------------------------------------------------------------
// Return on ad spend: one instrument, two daily lines read against a 30-day level
// ---------------------------------------------------------------------------

const CHART_W = 760;
const CHART_H = 232;
const MARGIN = { left: 44, right: 62, top: 16, bottom: 28 };
const PLOT = { y0: MARGIN.top, y1: CHART_H - MARGIN.bottom };
// The chart draws in real pixels rather than a 0-100 box scaled to fit, because a scaled
// viewBox shrinks the axis text with it: in the detail column the panel is about 515px wide,
// which would render the 10.5px labels at under 8px. So the drawing width follows the
// container and only the x-axis compresses; every stroke and every character stays true size.
function useMeasuredWidth(ref: React.RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(CHART_W);
  useLayoutEffect(() => {
    const measure = () => {
      const measured = ref.current?.clientWidth ?? 0;
      if (measured > 0) setWidth(measured);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref]);
  return width;
}

interface RoasPoint {
  date: string;
  twDaily: number | null; // Triple Whale, that day alone: one of the two lines drawn
  gDaily: number | null;  // Google, that day alone: the other
  g30: number | null;     // Google, trailing 30 days: the level its bidding pulls back toward
  tw7: number | null;     // Triple Whale, trailing week, sent by the collector
  g7: number | null;      // Google, trailing week
  spend: number;
  orders: number | null;
  // How many days each trailing window actually covers at this point in the series. Near the
  // start of a campaign's history the 30-day line is averaging fewer than 30 days, and a tooltip
  // that claimed otherwise would be stating a window that does not exist.
  days7: number;
  days30: number;
}

// A day with no spend has no return to report, so the line breaks there rather than being drawn
// through as if the campaign had earned nothing. A day that spent and sold nothing did earn
// nothing, and its point sits on the floor.
function dailyRoas(value: NullableNumber, spend: number): number | null {
  if (!(spend > 0)) return null;
  return isNumber(value) ? value : 0;
}

// Ratio of sums, never a mean of daily ratios. Averaging the daily ratios lets a $5 day weigh
// as much as a $500 one, which overstated the 30-day level by 6% to 18% on the three campaigns
// this was measured against, and that line is what the throttle warning reads.
function googleRatioOfSums(rows: CardRoasSeriesPoint[], index: number, windowSize: number): number | null {
  let spend = 0;
  let revenue = 0;
  for (let at = Math.max(0, index - windowSize + 1); at <= index; at += 1) {
    const row = rows[at];
    spend += row.spend;
    // googleRoasDaily is null exactly when the day had no spend, so such a day adds nothing
    // to either total rather than dropping out of the window and shortening it.
    revenue += isNumber(row.googleRoasDaily) ? row.googleRoasDaily * row.spend : 0;
  }
  return spend > 0 ? revenue / spend : null;
}

function buildRoasPoints(rows: CardRoasSeriesPoint[], ordersByDate: Map<string, number>): RoasPoint[] {
  return rows.map((row, index) => ({
    date: row.date,
    twDaily: dailyRoas(row.twRoasDaily, row.spend),
    gDaily: dailyRoas(row.googleRoasDaily, row.spend),
    g30: googleRatioOfSums(rows, index, 30),
    tw7: isNumber(row.twRoasRolling7) ? row.twRoasRolling7 : null,
    g7: googleRatioOfSums(rows, index, 7),
    spend: row.spend,
    orders: ordersByDate.get(row.date) ?? null,
    days7: Math.min(7, index + 1),
    days30: Math.min(30, index + 1),
  }));
}

// A campaign that started part way through the window has leading empty days. Drawing them
// leaves a stripe of normal range floating over nothing, so the frame starts at the first day
// that carries a number.
function trimLeading(points: RoasPoint[]): RoasPoint[] {
  const first = points.findIndex(point => isNumber(point.twDaily) || isNumber(point.gDaily) || isNumber(point.g30));
  return first <= 0 ? points : points.slice(first);
}

interface Scale {
  low: number;
  high: number;
  x0: number;
  x1: number;
  x: (index: number) => number;
  y: (value: number) => number;
}

function percentile(sortedAscending: number[], fraction: number): number {
  const at = (sortedAscending.length - 1) * fraction;
  const lower = Math.floor(at);
  const upper = Math.ceil(at);
  if (lower === upper) return sortedAscending[lower];
  return sortedAscending[lower] + (sortedAscending[upper] - sortedAscending[lower]) * (at - lower);
}

/**
 * `lines` are the plotted series, `extras` the reference levels (break-even, target, the shaded
 * range). Reference levels always fit; plotted values do not have to.
 *
 * Daily return is a ratio with spend on the bottom, so a quiet day that happened to convert can
 * print 40x and drag the top of the axis with it, squashing every ordinary day into the bottom
 * eighth of the panel. The axis therefore tops out at the 95th percentile of what is drawn: the
 * freak day runs off the top of the plot, where it is still visible as a line leaving the frame,
 * and its exact value is one hover away. Below eight points there is no distribution to speak of,
 * so nothing is capped and the 7-day view always shows every value in full.
 */
function makeScale(lines: NullableNumber[][], extras: NullableNumber[], pointCount: number, width: number): Scale {
  const plotted = lines.flat().filter(isNumber);
  const required = extras.filter(isNumber);
  const values = [...plotted, ...required];
  let low = values.length > 0 ? Math.min(...values) : 0;
  let high = values.length > 0 ? Math.max(...values) : 1;
  if (plotted.length >= 8) {
    const cap = Math.max(percentile([...plotted].sort((a, b) => a - b), 0.95), ...required, low);
    if (cap < high) high = cap;
  }
  const pad = (high - low) * 0.10 || 0.5;
  low -= pad;
  high += pad;
  if (low < 0) low = 0;
  const span = high - low || 1;
  const steps = Math.max(1, pointCount - 1);
  const x0 = MARGIN.left;
  const x1 = Math.max(x0 + 1, width - MARGIN.right);
  return {
    low,
    high,
    x0,
    x1,
    x: index => x0 + (index / steps) * (x1 - x0),
    y: value => PLOT.y1 - ((value - low) / span) * (PLOT.y1 - PLOT.y0),
  };
}

function yTicks(scale: Scale): number[] {
  const span = scale.high - scale.low;
  const step = span > 8 ? 4 : span > 4 ? 2 : span > 2 ? 1 : 0.5;
  const ticks: number[] = [];
  for (let value = Math.ceil(scale.low / step) * step; value <= scale.high; value += step) {
    ticks.push(Number(value.toFixed(2)));
  }
  return ticks;
}

interface EndLabel { x: number; y: number; value: number; color: string; weight: number }

function endLabel(values: NullableNumber[], scale: Scale, color: string, weight: number): EndLabel | null {
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (isNumber(value)) return { x: scale.x(index), y: scale.y(value), value, color, weight };
  }
  return null;
}

// Two lines ending within a few hundredths of each other would print their values on top of
// one another, so the labels get pushed apart vertically while the dots stay on the data.
function nudge(labels: EndLabel[]): EndLabel[] {
  const sorted = [...labels].sort((first, second) => first.y - second.y);
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index].y - sorted[index - 1].y < 13) sorted[index].y = sorted[index - 1].y + 13;
  }
  return sorted;
}

function KeySwatch({ color, style }: { color: string; style: 'solid' | 'fill' | 'dotted' | 'dashed' }) {
  if (style === 'fill') {
    return <Box component="i" sx={{ width: 16, height: 11, borderRadius: '3px', bgcolor: color, opacity: 0.14, display: 'inline-block', verticalAlign: 'middle', mr: 0.75 }} />;
  }
  // Drawn as SVG with the plot's own dash arrays rather than a CSS border, because the CSS
  // styles only approximate them: `2px dashed` over 17px renders three near-touching dashes
  // that read as a solid line, so the target swatch claimed to be a different kind of line
  // from the one on the chart (L, 2026-08-04). Same stroke here as there, so the key can be
  // matched to the plot by looking at it.
  const dash = style === 'dotted' ? '0.1 4.6' : style === 'dashed' ? '5 4' : undefined;
  return (
    <Box component="i" sx={{ display: 'inline-block', verticalAlign: 'middle', mr: 0.75, lineHeight: 0 }}>
      <svg width="24" height="4" viewBox="0 0 24 4" aria-hidden="true">
        <line x1="0.5" x2="23.5" y1="2" y2="2" stroke={color} strokeWidth={style === 'dotted' ? 2.6 : 2.2} strokeDasharray={dash} strokeLinecap={style === 'dotted' ? 'round' : 'butt'} />
      </svg>
    </Box>
  );
}

// The clip rects are referenced by id, so two charts on one page would otherwise share them and
// one would be masked against the other's break-even. React 18's useId would do this, but the repo
// is still on the React 17 type definitions.
let clipSeq = 0;

function RoasChart({ points, band, breakeven, target, store }: {
  points: RoasPoint[];
  band?: CardBand;
  breakeven: number | null;
  target: number | null;
  store: string | null;
}) {
  const seq = useRef((clipSeq += 1)).current;
  const belowId = `roas-below-${seq}`;
  const plotId = `roas-plot-${seq}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const width = useMeasuredWidth(containerRef);
  const [hover, setHover] = useState<number | null>(null);

  // The shaded range is the ROAS alert's own rule: the median of its baseline weeks, give or take
  // that campaign's normal week-to-week swing. A week outside it is a week the alert calls a
  // deviation, so the picture and the alert cannot disagree. usual and wobble are null together
  // when the campaign has too little history for that alert to judge it, and absent together on
  // payloads written before the collector sent them; either way the chart draws no range rather
  // than substituting a different measurement.
  const usual = band?.usual;
  const wobble = band?.wobble;
  const spread = isNumber(usual) && isNumber(wobble) && wobble > 0
    ? { low: usual * (1 - wobble), high: usual * (1 + wobble) }
    : null;
  // The alert's baseline is eight weeks when it has eight weeks. A younger campaign is judged
  // against fewer, and the key says how many rather than claiming a history that is not there.
  const spreadWeeks = isNumber(band?.baselineWeeks) ? band!.baselineWeeks! : 8;

  const twDaily = points.map(point => point.twDaily);
  const gDaily = points.map(point => point.gDaily);
  const g30 = points.map(point => point.g30);
  const scale = makeScale([twDaily, gDaily, g30], [breakeven, target, spread?.low, spread?.high], points.length, width);
  // A value above the capped axis draws off the top of the plot on purpose, but its end dot and
  // end label are furniture rather than data and would land in the margin, so those get pinned.
  const clampY = (value: number): number => Math.max(PLOT.y0, Math.min(PLOT.y1, scale.y(value)));

  const twPath = linearPath(twDaily, scale.x, scale.y);
  const lastTw = latestValue(twDaily);
  const labels = [
    endLabel(twDaily, scale, isNumber(breakeven) && isNumber(lastTw) && lastTw < breakeven ? BAD : TW, 700),
    endLabel(gDaily, scale, G_WEEK, 600),
    endLabel(g30, scale, G_LEVEL, 500),
  ].filter((label): label is EndLabel => label != null)
    .map(label => ({ ...label, y: clampY(label.value) }));

  const dateIndexes = [0, Math.floor((points.length - 1) / 2), points.length - 1];
  const hovered = hover != null ? points[hover] : null;
  const hoverLeft = hover != null ? (scale.x(hover) / width) * 100 : 0;

  const handleMove = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = rect.width > 0 ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)) : 0;
    setHover(Math.round(fraction * Math.max(0, points.length - 1)));
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${CHART_H}`} role="img" aria-label="Return on ad spend, by day" style={{ display: 'block', width: '100%', height: CHART_H }}>
        <defs>
          <clipPath id={plotId}>
            <rect x={scale.x0} y={PLOT.y0} width={scale.x1 - scale.x0} height={PLOT.y1 - PLOT.y0} />
          </clipPath>
          {isNumber(breakeven) && (
            <clipPath id={belowId}>
              <rect x={scale.x0} y={scale.y(breakeven)} width={scale.x1 - scale.x0} height={Math.max(0, PLOT.y1 - scale.y(breakeven))} />
            </clipPath>
          )}
        </defs>

        {/* Normal range, tinted to the blue line it belongs to. Named in the key beneath the
            chart rather than on the plot, so no text ever sits over the data. */}
        {spread && (
          <rect x={scale.x0} y={scale.y(spread.high)} width={scale.x1 - scale.x0} height={Math.max(0, scale.y(spread.low) - scale.y(spread.high))} fill={TW} fillOpacity="0.085" />
        )}

        {yTicks(scale).map(value => (
          <g key={`tick-${value}`}>
            <line x1={scale.x0} x2={scale.x1} y1={scale.y(value)} y2={scale.y(value)} stroke={BORDER} strokeWidth="1" />
            <text x={scale.x0 - 8} y={scale.y(value) + 3.5} fontSize="10.5" fill={FAINT} textAnchor="end" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}x</text>
          </g>
        ))}

        {isNumber(target) && (
          <line x1={scale.x0} x2={scale.x1} y1={scale.y(target)} y2={scale.y(target)} stroke={FAINT} strokeWidth="1.2" strokeDasharray="5 4" />
        )}
        {isNumber(breakeven) && (
          <line x1={scale.x0} x2={scale.x1} y1={scale.y(breakeven)} y2={scale.y(breakeven)} stroke={INK} strokeWidth="2.4" strokeDasharray="0.1 4.6" strokeLinecap="round" />
        )}

        <g clipPath={`url(#${plotId})`}>
          {/* The 30-day level is dotted, which puts it in the same family as break-even and
              target: a line you read against, not a measurement of the day. */}
          <path d={smoothPath(g30, scale.x, scale.y)} fill="none" stroke={G_LEVEL} strokeWidth="2.6" strokeDasharray="0.1 4.6" strokeLinejoin="round" strokeLinecap="round" />
          <path d={linearPath(gDaily, scale.x, scale.y)} fill="none" stroke={G_WEEK} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />

          <path d={twPath} fill="none" stroke={TW} strokeWidth="2.7" strokeLinejoin="round" strokeLinecap="round" />
          {isNumber(breakeven) && (
            <path d={twPath} fill="none" stroke={BAD} strokeWidth="2.7" strokeLinejoin="round" strokeLinecap="round" clipPath={`url(#${belowId})`} />
          )}
        </g>

        {labels.map(label => (
          // the reference line gets a hollow dot, the two judged lines get solid ones
          <circle key={`dot-${label.color}`} cx={label.x} cy={clampY(label.value)} r={label.weight === 500 ? 3 : 3.2} fill={label.weight === 500 ? PAPER : label.color} stroke={label.color} strokeWidth={label.weight === 500 ? 1.6 : 0} />
        ))}
        {nudge(labels).map(label => (
          <text key={`end-${label.color}`} x={scale.x1 + 8} y={label.y + 3.5} fontSize="10.5" fontWeight={label.weight} fill={label.color} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {label.value.toFixed(2)}x
          </text>
        ))}

        {hover != null && (
          <line x1={scale.x(hover)} x2={scale.x(hover)} y1={PLOT.y0} y2={PLOT.y1} stroke={FAINT} strokeWidth="1" />
        )}

        {dateIndexes.map((index, position) => (
          <text key={`x-${position}`} x={scale.x(index)} y={PLOT.y1 + 17} fontSize="10.5" fill={FAINT} textAnchor={position === 0 ? 'start' : position === 2 ? 'end' : 'middle'}>
            {dateLabel(points[index]?.date)}
          </text>
        ))}
      </svg>

      <Box
        aria-hidden="true"
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
        sx={{
          position: 'absolute',
          left: `${scale.x0}px`,
          right: `${Math.max(0, width - scale.x1)}px`,
          top: `${PLOT.y0}px`,
          bottom: `${CHART_H - PLOT.y1}px`,
          cursor: 'crosshair',
        }}
      />

      {hovered && (
        <Box sx={{
          position: 'absolute',
          top: 6,
          ...(hoverLeft > 50 ? { right: `${100 - hoverLeft}%`, mr: 1.25 } : { left: `${hoverLeft}%`, ml: 1.25 }),
          pointerEvents: 'none',
          bgcolor: INK,
          color: PAPER,
          borderRadius: 1,
          px: 1.25,
          py: 1,
          fontSize: 11,
          lineHeight: 1.5,
          whiteSpace: 'nowrap',
          boxShadow: '0 4px 14px rgba(15,23,42,0.22)',
          zIndex: 5,
        }}>
          {/* The day comes first, because the day is what the two solid lines draw and what the
              crosshair is pointing at. The trailing averages are context for it and sit below the
              rule, each under the span of days it actually covers. */}
          <Typography sx={{ color: '#CBD5E1', fontSize: 10, mb: 0.5 }}>{rangeLabel(hovered.date, 1)}</Typography>
          {[
            { label: 'Triple Whale', color: TW, text: roasExact(hovered.twDaily) },
            { label: 'Google', color: G_WEEK, text: roasExact(hovered.gDaily) },
          ].map(row => (
            <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: row.color, boxShadow: '0 0 0 1px rgba(255,255,255,0.5)' }} />
              <span>{row.label}</span>
              <Typography component="b" sx={{ fontWeight: 700, ml: 'auto', pl: 1.5, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{row.text}</Typography>
            </Box>
          ))}
          {[
            { label: 'Spend', text: moneyText(hovered.spend, store) },
            { label: 'Orders', text: hovered.orders != null ? String(hovered.orders) : '-' },
          ].map(row => (
            <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: '14px' }}>
              <span>{row.label}</span>
              <Typography component="b" sx={{ fontWeight: 700, ml: 'auto', pl: 1.5, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{row.text}</Typography>
            </Box>
          ))}

          <Typography sx={{ color: '#CBD5E1', fontSize: 10, mt: 0.75, mb: 0.25, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
            {rangeLabel(hovered.date, hovered.days7)}
          </Typography>
          {[
            { label: 'Triple Whale', text: roasExact(hovered.tw7) },
            { label: 'Google', text: roasExact(hovered.g7) },
          ].map(row => (
            <Box key={`w7-${row.label}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <span>{row.label}</span>
              <Typography component="b" sx={{ fontWeight: 700, ml: 'auto', pl: 1.5, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{row.text}</Typography>
            </Box>
          ))}
          <Typography sx={{ color: '#CBD5E1', fontSize: 10, mt: 0.5, mb: 0.25 }}>{rangeLabel(hovered.date, hovered.days30)}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box component="span" sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: G_LEVEL, boxShadow: '0 0 0 1px rgba(255,255,255,0.5)' }} />
            <span>Google</span>
            <Typography component="b" sx={{ fontWeight: 700, ml: 'auto', pl: 1.5, fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>{roasExact(hovered.g30)}</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.75, mt: 1.25, fontSize: 11.5, color: MUTED, alignItems: 'center' }}>
        {/* Each line is named by its source and its window, and no figure appears in the key: the
            axis and the end labels already carry every number on this chart, and repeating them
            here made the key long enough to wrap. */}
        <Box component="span"><KeySwatch color={TW} style="solid" />Triple Whale</Box>
        {spread && <Box component="span"><KeySwatch color={TW} style="fill" />Recent {spreadWeeks}-week range</Box>}
        <Box component="span"><KeySwatch color={G_WEEK} style="solid" />Google</Box>
        <Box component="span"><KeySwatch color={G_LEVEL} style="dotted" />Google 30-day</Box>
        {isNumber(breakeven) && <Box component="span"><KeySwatch color={INK} style="dotted" />Break-even</Box>}
        {isNumber(target) && <Box component="span"><KeySwatch color={FAINT} style="dashed" />Target</Box>}
      </Box>
    </Box>
  );
}

const rangeOrder: RangeDays[] = [7, 30, 90];

const controlButtonSx = {
  border: 0,
  borderRadius: 0,
  bgcolor: PAPER,
  color: MUTED,
  minWidth: 0,
  px: 1.625,
  py: 0.625,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1.4,
  textTransform: 'none',
  '& + &': { borderLeft: `1px solid ${BORDER}` },
  '&:hover': { bgcolor: '#F8FAFC' },
  '&:focus-visible': { outline: `2px solid ${INDIGO}`, outlineOffset: -2 },
};

export default function TrendCharts({ series, band, breakeven, target, store }: {
  series?: CardSeries;
  band?: CardBand;
  breakeven: number | null;
  target: number | null;
  store: string | null;
}) {
  const [range, setRange] = useState<RangeDays>(30);
  const roasRows = useMemo(() => series?.roas ?? [], [series?.roas]);
  // Orders come from the funnel series, which is keyed by the same dates, so the tooltip can say
  // how many sales are behind a day's return without the collector sending it twice.
  const ordersByDate = useMemo(() => {
    const map = new Map<string, number>();
    (series?.funnelDaily ?? []).forEach((row: CampaignFunnelDailyRow) => map.set(row.date, row.orders));
    return map;
  }, [series?.funnelDaily]);
  // Rolling windows are computed over the whole series and only then cut to the visible range.
  // Computing them on the cut would leave a 7-day view with no 30-day line at all.
  const roasPoints = useMemo(
    () => trimLeading(sliceRange(buildRoasPoints(roasRows, ordersByDate), range)),
    [roasRows, ordersByDate, range],
  );

  if (!series || roasRows.length === 0) return null;

  return (
    <Box sx={{ mb: 1, mt: 2.5 }}>
      {/* Always drawn. The trend is how a reader decides whether anything is wrong at all, so
          putting it behind a toggle asked them to go looking for the context they need to read
          everything below it (L, 2026-09-04). */}
      <Box sx={{ p: '18px 20px 20px', border: `1px solid ${BORDER}`, borderRadius: '12px', bgcolor: PAPER, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.75, flexWrap: 'wrap' }}>
          {/* A title rather than a headline number: the end labels carry the values, and a bare
              figure at the top would be the one number on the panel with nothing saying which
              day or which window it covers. */}
          <Typography component="h4" sx={{ fontSize: 14, fontWeight: 650, color: INK, m: 0 }}>ROAS over time</Typography>
          <Box role="group" aria-label="Trend range" sx={{ display: 'inline-flex', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
            {rangeOrder.map(days => (
              <Button key={days} aria-pressed={range === days} onClick={() => setRange(days)} sx={{ ...controlButtonSx, ...(range === days ? { bgcolor: INK, color: PAPER, '&:hover': { bgcolor: INK } } : {}) }}>
                {days}d
              </Button>
            ))}
          </Box>
        </Box>
        {roasPoints.length > 0
          ? <RoasChart key={range} points={roasPoints} band={band} breakeven={breakeven} target={target} store={store} />
          : <Typography sx={{ fontSize: 12.5, color: MUTED, py: 3 }}>No return data for this campaign yet.</Typography>}
      </Box>
    </Box>
  );
}
